import { useEffect } from 'react';

const FOCUSABLE_SELECTORS =
	'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Traps keyboard focus within the given panel element when isOpen is true.
 * Tab from last focusable wraps to first; Shift+Tab from first wraps to last.
 *
 * @param {React.RefObject} panelRef - Ref to the panel DOM element.
 * @param {boolean} isOpen - Whether the panel is currently open.
 */
const useFocusTrap = (panelRef, isOpen) => {
	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}

		const handleKeyDown = (event) => {
			if (event.key !== 'Tab') {
				return;
			}

			const panel = panelRef.current;
			if (!panel) {
				return;
			}

			// querySelectorAll returns a static NodeList, which is correct here:
			// we re-query on every Tab keypress to always reflect the current DOM.
			const focusable = Array.from(
				panel.querySelectorAll(FOCUSABLE_SELECTORS)
			).filter((el) => !el.closest('[inert]'));
			if (focusable.length === 0) {
				return;
			}

			const first = focusable[0];
			const last = focusable[focusable.length - 1];

			if (event.shiftKey) {
				if (document.activeElement === first) {
					event.preventDefault();
					last.focus();
				}
			} else if (document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [isOpen, panelRef]);
};

export default useFocusTrap;
